import { useState, useMemo, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/components/feature/MovieCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { categories, allVideos, sortOptions } from '@/mocks/videos';
import { siteConfig } from '@/mocks/site';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState('default');
  const [activeSlug, setActiveSlug] = useState(slug || 'dongzuo');

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeSlug);
  const categoryVideos = useMemo(() => {
    let videos = allVideos.filter((v) => v.category === activeSlug);
    if (sortBy === 'rating') {
      videos = [...videos].sort((a, b) => b.rating - a.rating);
    } else if (sortBy === 'year') {
      videos = [...videos].sort((a, b) => b.year - a.year);
    }
    return videos;
  }, [activeSlug, sortBy]);

  const webPageSchema = {
    name: currentCategory ? `${currentCategory.name} - ${siteConfig.name}` : siteConfig.name,
    description: currentCategory ? currentCategory.description : siteConfig.description,
    url: `${siteConfig.url}/category/${activeSlug}`,
  };

  return (
    <>
      <SchemaOrg type="WebPage" data={webPageSchema} />

      <Navbar />

      <main className="pt-16 min-h-screen bg-background-50">
        <section className="py-8 md:py-12 bg-background-900">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: currentCategory?.name || '分类' },
              ]}
            />
            <div className="mt-4">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-white mb-2">
                {currentCategory?.name || '分类'}
              </h1>
              <p className="text-foreground-300 text-sm max-w-xl">
                {currentCategory?.description || ''}
              </p>
            </div>
          </div>
        </section>

        <section className="py-6 border-b border-background-200 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex flex-wrap items-center gap-2">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-xs`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-8 md:py-12 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-foreground-500">
                共找到 <strong className="text-foreground-800">{categoryVideos.length}</strong> 部作品
              </p>
              <div className="flex items-center gap-2">
                <span className="text-xs text-foreground-500">排序：</span>
                {sortOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setSortBy(opt.value)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                      sortBy === opt.value
                        ? 'bg-primary-500 text-white'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {categoryVideos.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5" data-product-shop>
                {categoryVideos.map((video) => (
                  <MovieCard
                    key={video.id}
                    id={video.id}
                    title={video.title}
                    category={video.category}
                    year={video.year}
                    rating={video.rating}
                    type={video.type}
                    posterUrl={video.posterUrl}
                    isNew={video.isNew}
                    categoryName={currentCategory?.name}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
                  <i className="ri-inbox-line text-2xl"></i>
                </div>
                <p className="text-foreground-500">该分类暂无内容，请浏览其他分类</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}